/**
 * Materials by day — Board 3 A (`docs/visit-page.md` §8.4): the crew's
 * morning, a column per day. **No money** — it is the crew's list.
 *
 * Groups follow the morning and are drawn only when they have rows: *Load at
 * the yard* (once done *Loaded at the yard*), *Pick up on the way* (one per
 * stop), *Already on site*, then — once the day is done — *Used* and *Bought
 * in the field*. The server sends the groups in that order; the page draws
 * them.
 */
import type {
  DayChip, MaterialDayColumn, MaterialDayGroup, MaterialDayNote, MaterialDayRow, MaterialsByDay, VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { StatusChip } from '../components/primitives.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { COMMON, MATERIALS, ORDER } from './copy.js'
import { dayText, timeIn } from './format.js'
import { MaterialChip, RowButton, count, qtyText } from './materials-parts.js'

export function ByDay({ data, read, phone, onOrder, canEdit }: {
  data: MaterialsByDay
  read: Pick<VisitRead, 'timeZone'>
  phone: boolean
  onOrder: (lineId: string) => void
  canEdit: boolean
}) {
  return (
    <div className={cn('flex gap-3', phone ? 'flex-col' : 'items-start')}>
      {data.days.map((column) => (
        <DayColumn
          key={column.day.index}
          column={column}
          read={read}
          onOrder={onOrder}
          canEdit={canEdit}
        />
      ))}
    </div>
  )
}

/** *Closed out · On site · 2 not ready* — the day's chip, as By day words it. */
function ByDayChip({ chip }: { chip: DayChip }) {
  const words = chip.kind === 'closed_out' ? COMMON.closedOut
    : chip.kind === 'on_site' || chip.kind === 'on_the_clock' ? COMMON.onSite
      : chip.kind === 'prep' ? MATERIALS.byDay.notReady(count(chip.notReady))
        : chip.kind === 'ready' ? COMMON.ready
          : chip.kind === 'scheduled' ? COMMON.scheduled : COMMON.skipped
  return <StatusChip group="visitDay" status={chip.kind} className="shrink-0 whitespace-nowrap">{words}</StatusChip>
}

/** One day's column — exported for the crew sheet's print page (Slice 1). */
export function DayColumn({ column, read, onOrder, canEdit }: {
  column: MaterialDayColumn
  read: Pick<VisitRead, 'timeZone'>
  onOrder: (lineId: string) => void
  canEdit: boolean
}) {
  const today = column.day.relative === 'today'
  return (
    <section
      data-day={column.day.index}
      className={cn(
        'flex min-w-0 flex-1 basis-0 flex-col gap-2.5 rounded-record border border-line p-3.5',
        today ? 'bg-green-band-bg' : 'bg-surface',
      )}
    >
      <div className="flex items-center gap-1.5">
        <b className="text-[14px]">{COMMON.dayN(String(column.day.index))}</b>
        <span className="text-[12.5px] text-text-muted">{dayText(column.day.date, 'date')}</span>
        {column.day.relative && (
          <span
            className={cn(
              'text-[10.5px] font-bold uppercase tracking-[.04em]',
              column.day.relative === 'today' ? 'text-teal-band-text' : 'text-warn',
            )}
          >
            {COMMON[column.day.relative]}
          </span>
        )}
        <span className="flex-1" />
        <ByDayChip chip={column.chip} />
      </div>
      {column.groups.map((group, index) => (
        <DayGroup
          key={`${group.kind}-${index}`}
          group={group}
          read={read}
          onOrder={onOrder}
          canEdit={canEdit}
        />
      ))}
    </section>
  )
}

/** The glyph a group wears in its head, and its rows unless they say otherwise. */
function groupGlyph(group: MaterialDayGroup): VisitGlyphName {
  switch (group.kind) {
    case 'load': return group.done ? 'truck' : 'yard'
    case 'pick_up': return 'pickup'
    case 'on_site': return 'site'
    case 'used': return 'check'
    case 'bought': return 'receipt'
  }
}

/** A group's title and the line under it. */
function groupWords(group: MaterialDayGroup, tz: string): { title: string; sub: string } {
  const B = MATERIALS.byDay
  switch (group.kind) {
    case 'load':
      return group.done
        ? { title: B.loaded, sub: group.truck ? COMMON.dot(timeIn(tz, group.at, 'ampm'), group.truck) : timeIn(tz, group.at, 'ampm') }
        : { title: B.load, sub: timeIn(tz, group.at, 'ampm') }
    case 'pick_up': {
      const parts = [group.vendor.name, group.vendor.line1, group.at ? timeIn(tz, group.at, 'clock') : null, group.picker?.name ?? null]
        .filter((part): part is string => Boolean(part))
      return { title: B.pickUp, sub: parts.reduce((all, part) => COMMON.dot(all, part)) }
    }
    case 'on_site':
      return { title: B.onSite, sub: group.deliveredOn ? B.delivered(dayText(group.deliveredOn, 'day')) : '' }
    case 'used':
      return {
        title: B.used,
        sub: group.closeOut ? B.fromCloseOut(group.closeOut.by.firstName, timeIn(tz, group.closeOut.at, 'ampm')) : '',
      }
    case 'bought':
      return { title: B.bought, sub: B.boughtSub }
  }
}

function DayGroup({ group, read, onOrder, canEdit, asDrawn }: {
  group: MaterialDayGroup
  read: Pick<VisitRead, 'timeZone'>
  onOrder: (lineId: string) => void
  canEdit: boolean
  asDrawn?: string
}) {
  const words = groupWords(group, read.timeZone)
  const glyph = groupGlyph(group)
  /* Board 3 C draws the pickup that the kit just joined on its own. */
  const justAdded = group.kind === 'pick_up' && group.rows.some((row) => row.note?.kind === 'added')
  return (
    <div
      className="overflow-hidden rounded-[12px] border border-line bg-surface"
      data-as-drawn={asDrawn ?? (justAdded ? 'vp-03 C group' : undefined)}
    >
      <div className="flex items-start gap-2 bg-surface-alt px-3 py-[9px]">
        <span className="mt-px inline-flex text-accent"><VisitGlyph name={glyph} size={15} stroke={2} /></span>
        <div className="flex flex-col">
          <b className="text-[12.5px]">{words.title}</b>
          {words.sub && <span className="text-[11.5px] text-text-muted">{words.sub}</span>}
        </div>
      </div>
      {group.rows.map((row) => (
        <DayRow
          key={row.lineId}
          row={row}
          glyph={glyph}
          read={read}
          onOrder={onOrder}
          canEdit={canEdit}
        />
      ))}
    </div>
  )
}

function DayRow({ row, glyph, read, onOrder, canEdit }: {
  row: MaterialDayRow
  glyph: VisitGlyphName
  read: Pick<VisitRead, 'timeZone'>
  onOrder: (lineId: string) => void
  canEdit: boolean
}) {
  const needs = row.order
  const rowGlyph: VisitGlyphName = needs ? 'warn' : row.note?.kind === 'arriving' ? 'clock' : glyph
  const restOf = row.note?.kind === 'rest_of'
  return (
    <div
      data-line={row.lineId}
      className={cn('flex items-center gap-2.5 border-t border-line-faint px-3 py-[9px]', needs && 'bg-amber-row')}
    >
      <span className={cn('inline-flex', needs ? 'text-warn' : 'text-text-muted')}>
        <VisitGlyph name={rowGlyph} size={15} stroke={2} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-w-0 items-center gap-1.5">
          <b className="truncate text-[12.5px] font-semibold">{row.continued ? row.names.shortName : row.names.name}</b>
          <span className="shrink-0 text-[12px] text-text-muted">
            {restOf ? MATERIALS.byDay.restOf(qtyText(row.qty.qty, row.qty.unit)) : qtyWords(row)}
          </span>
        </div>
        {row.note && <span className="text-[11.5px] text-text-muted">{noteWords(row.note, read.timeZone)}</span>}
      </div>
      {needs && canEdit
        ? <RowButton primary onClick={() => onOrder(row.lineId)}>{MATERIALS.order}</RowButton>
        : row.chip && <MaterialChip state={row.chip} />}
    </div>
  )
}

/** *12 of 18 t* · *6 t* · *96*. */
function qtyWords(row: MaterialDayRow): string {
  return row.qty.of !== null
    ? COMMON.of(count(row.qty.qty), qtyText(row.qty.of, row.qty.unit))
    : qtyText(row.qty.qty, row.qty.unit)
}

/** The line under a row's name (Board 3 A's notes). */
function noteWords(note: MaterialDayNote, tz: string): string {
  const B = MATERIALS.byDay
  switch (note.kind) {
    case 'left_for':
      return B.leftFor(qtyText(note.qty, note.unit), String(note.day))
    case 'bought':
      return note.forDay !== null && note.by && note.receipt === 'to_review'
        ? B.boughtFor(String(note.forDay), note.vendor, note.by.name)
        : note.by ? COMMON.dot(note.vendor, note.by.name) : note.vendor
    case 'delivered':
      return note.where && note.po ? COMMON.dot(note.where, note.po.number) : note.where ?? note.po?.number ?? ''
    case 'rest_of':
      return note.po?.number ?? ''
    case 'held':
      return B.held
    case 'arriving':
      return B.arriving(note.relative ? COMMON[note.relative] : timeIn(tz, note.at, 'day'), timeIn(tz, note.at, 'hourAmpm'), note.po.number)
    case 'po':
      return note.po.number
    case 'added':
      return ORDER.addedLine(note.po.number, timeIn(tz, note.at, 'ampm'), note.by.name)
    case 'not_ordered':
      return note.neededBy ? B.notOrderedYet(timeIn(tz, note.neededBy, 'clock')) : ''
  }
}
